/*
Cold-start splash overlay — wordmark + rotating informational tip,
shown once per page-load over the map screen.

Rotation rules (mirror Android SplashTipsController.kt):
- First launch (splashIntroSeen=false): cycle through the ordered intro
  tips. After the splash closes, mark splashIntroSeen=true. Most users
  will only see 1–2 intros per session given the splash is brief — that's
  intentional, the goal is to expose them across the first few sessions.
- Subsequent launches: pick a random tip from the full bank, with
  player-snapshot placeholder substitution. Rotation cadence 5 s (down
  from Android's 7 s — PWA splashes are shorter overall).
- Tap-to-dismiss anywhere on the overlay.

Player snapshot is built once at mount (captureCount, speciesCount,
daysSinceFirst). remarquableCount is NOT computed here because it would
require parsing the 32 MB GeoJSON; tips that reference it are simply
filtered out from random rotation.
*/

import { ensureSplashTips, getIntroTips, getAllTips } from '../../data/splashTips'
import { isSplashIntroSeen, markSplashIntroSeen } from '../../data/onboardingStore'
import { listCaptures } from '../../data/captureRepository'

const kRotationMs: number = 5000  // cadence per tip
const kMaxDisplayMs: number = 4500  // hard auto-dismiss cap
const kFadeOutMs: number = 320  // matches CSS transition duration for opacity

interface PlayerSnapshot
{
	captureCount: number
	speciesCount: number
	daysSinceFirst: number | null  // null if no captures yet
	remarquableCount: number | null  // intentionally null in the PWA splash (would need a 32 MB parse to fill)
}

let fSessionShown: boolean = false  // module-level: splash only shows once per page-load

/*
HTML markup for the splash. Rendered into the host root by the caller
before `startSplash` is invoked. Uses data-open=true by default — the
splash is always shown when its markup is present.
*/
export function renderSplashHtml(): string
{
	return `
		<div class="splash" id="splash" data-open="true" role="status">
			<div class="splash-content">
				<div class="splash-glyph" aria-hidden="true">🌳</div>
				<h1 class="display splash-wordmark">CanoPaname</h1>
				<div class="splash-tip" id="splash-tip"></div>
			</div>
		</div>
	`
}

/*
Start the rotation + auto-dismiss timers. Returns a controller that
exposes `dismiss()` for callers that want to dismiss earlier (e.g.
on a route change). The splash auto-fades after kMaxDisplayMs.

Skipped if the splash has already been shown this session — repeat
mounts of MapScreen (via in-app navigation) won't see it again.
*/
export async function startSplash(inRoot: HTMLElement): Promise<{ dismiss: () => void }>
{
	const vSplash = inRoot.querySelector<HTMLElement>('#splash')
	if (!vSplash) return { dismiss: () => { /* already gone */ } }

	if (fSessionShown)
	{
		// Skip: this is an in-session remount of the map. Hide instantly.
		hideAndRemove(vSplash)
		return { dismiss: () => { /* already gone */ } }
	}
	fSessionShown = true

	let vRotationTimer: number | null = null  // setInterval id for tip rotation
	let vDismissTimer: number | null = null  // setTimeout id for auto-dismiss
	let vDismissed: boolean = false  // single-fire guard

	const vDismiss = (): void => {
		if (vDismissed) return
		vDismissed = true
		if (vRotationTimer !== null) window.clearInterval(vRotationTimer)
		if (vDismissTimer !== null) window.clearTimeout(vDismissTimer)
		hideAndRemove(vSplash)
	}

	// Tap anywhere on the overlay to dismiss early.
	vSplash.addEventListener('click', vDismiss)

	try
	{
		await ensureSplashTips()
		const vIntroSeen = await isSplashIntroSeen()
		const vSnap = await buildPlayerSnapshot()  // captureCount, etc., for placeholder substitution

		const vSequence = pickSequence(vIntroSeen, vSnap)  // ordered list of tip TEXT (already substituted)
		if (vSequence.length === 0)
		{
			// No tips usable (rare — bad data file or all tips need missing
			// placeholders). Just show the wordmark and dismiss on schedule.
			vDismissTimer = window.setTimeout(vDismiss, kMaxDisplayMs)
			return { dismiss: vDismiss }
		}

		let vIndex = 0
		paintTip(vSplash, vSequence[vIndex])
		if (vSequence.length > 1)
		{
			vRotationTimer = window.setInterval(() => {
				vIndex = (vIndex + 1) % vSequence.length
				paintTip(vSplash, vSequence[vIndex])
			}, kRotationMs)
		}
		vDismissTimer = window.setTimeout(vDismiss, kMaxDisplayMs)

		// Mark intro seen once the splash is up — even if the user dismisses
		// after one tip, we've satisfied the "saw it once" gate.
		if (!vIntroSeen) void markSplashIntroSeen().catch(() => { /* non-critical */ })
	}
	catch (vErr)
	{
		console.warn('Splash boot failed', vErr)
		vDismissTimer = window.setTimeout(vDismiss, kMaxDisplayMs)
	}

	return { dismiss: vDismiss }
}

/*
Build the ordered sequence of tip texts to display. Intro mode emits the
intro list verbatim (ordered). Random mode picks up to 3 distinct tips
from the full bank that pass placeholder substitution — enough to cover
the auto-dismiss window if the user lingers.
*/
function pickSequence(inIntroSeen: boolean, inSnap: PlayerSnapshot): string[]
{
	if (!inIntroSeen)
	{
		return getIntroTips()
			.map((inT) => substitute(inT.text, inSnap))
			.filter((inS): inS is string => inS !== null)
	}

	const vAll = getAllTips()
	const vShuffled = shuffle(vAll)  // copy + Fisher-Yates
	const vOut: string[] = []
	for (const vTip of vShuffled)
	{
		if (vOut.length >= 3) break  // cap — kMaxDisplayMs / kRotationMs ≈ 1; 3 is generous slack
		const vText = substitute(vTip.text, inSnap)
		if (vText !== null) vOut.push(vText)
	}
	return vOut
}

/*
Substitute `{name}` placeholders in `inText` using the player snapshot.
Returns null if any placeholder maps to a missing value — the caller
treats this as "skip this tip" so the user never sees a dangling `{xxx}`.
*/
function substitute(inText: string, inSnap: PlayerSnapshot): string | null
{
	let vMissing = false  // becomes true when any placeholder can't be resolved
	const vResult = inText.replace(/\{(\w+)\}/g, (_, inKey: string) => {
		const vValue = (inSnap as unknown as Record<string, unknown>)[inKey]
		if (vValue === null || vValue === undefined)
		{
			vMissing = true
			return ''
		}
		return String(vValue)
	})
	return vMissing ? null : vResult
}

/*
Build the player snapshot used for placeholder substitution. Cheap —
just one IDB read + a sort.
*/
async function buildPlayerSnapshot(): Promise<PlayerSnapshot>
{
	try
	{
		const vCaptures = await listCaptures()  // already chronological
		const vSpecies = new Set<number>()  // distinct speciesIndex values
		for (const vCap of vCaptures) vSpecies.add(vCap.speciesIndex)
		const vFirst = vCaptures.length > 0 ? vCaptures[0].timestamp : null  // earliest capture timestamp
		const vDaysSinceFirst = vFirst !== null
			? Math.max(1, Math.floor((Date.now() - vFirst) / (24 * 3600 * 1000)))
			: null
		return {
			captureCount: vCaptures.length,
			speciesCount: vSpecies.size,
			daysSinceFirst: vDaysSinceFirst,
			remarquableCount: null,
		}
	}
	catch
	{
		return { captureCount: 0, speciesCount: 0, daysSinceFirst: null, remarquableCount: null }
	}
}

function shuffle<T>(inArr: readonly T[]): T[]
{
	const vOut = [...inArr]  // shallow copy; we don't mutate the caller's array
	for (let vI = vOut.length - 1; vI > 0; vI--)
	{
		const vJ = Math.floor(Math.random() * (vI + 1))
		;[vOut[vI], vOut[vJ]] = [vOut[vJ], vOut[vI]]
	}
	return vOut
}

function paintTip(inSplash: HTMLElement, inText: string): void
{
	const vEl = inSplash.querySelector<HTMLElement>('#splash-tip')
	if (!vEl) return
	// Quick fade-out / fade-in on rotate so changes are gentle. Done via
	// data-fading toggle so CSS owns the transitions.
	vEl.dataset.fading = 'true'
	window.setTimeout(() => {
		vEl.textContent = inText
		vEl.dataset.fading = 'false'
	}, 180)
}

function hideAndRemove(inSplash: HTMLElement): void
{
	inSplash.setAttribute('data-open', 'false')
	window.setTimeout(() => { inSplash.remove() }, kFadeOutMs)
}
